import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatoDinero } from "../../lib/money";
import { T, CLASE_ESTADO, datosDelError, descargarCfdi } from "../../lib/cfdi";
import FichaFiscal from "./FichaFiscal";
import CancelarCfdi from "./CancelarCfdi";
import PaqueteCfdi from "./PaqueteCfdi";
import { FileText, RefreshCw, Mail, Ban, Download } from "lucide-react";

// La factura (CFDI) de un cobro ya guardado — CL.4 C. Sólo se monta si el backend dijo que
// la facturación a pacientes está disponible para esta sesión (bandera + capacidad).
// 🔴 La pantalla NO calcula nada fiscal: la vista previa (claves SAT, IVA, forma de pago,
//    receptor, modo, cupo) la arma el servidor y aquí se pinta tal cual.
// Textos: lib/cfdi.js (# Copy aprobado por Jesús (4 Oct 2026)) salvo «Facturar», que ya existía en este modal.
const VIVOS = ["reservado", "timbrando", "vigente", "cancelacion_solicitada"];
const btn = "px-3 py-1.5 text-xs rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-60";

export default function FacturarCobro({ cobro, puede, onCambio }) {
  const [cfdis, setCfdis] = useState(null);
  const [modo, setModo] = useState("lista");          // lista | facturar | cancelar
  const [receptor, setReceptor] = useState("paciente");
  const [enviarCorreo, setEnviarCorreo] = useState(true);
  const [correo, setCorreo] = useState("");
  const [vista, setVista] = useState(null);
  const [error, setError] = useState(null);            // {detail, code}
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/cfdis", { params: { cobro_id: cobro.id } });
      setCfdis(data?.cfdis || []);
    } catch (e) {
      setCfdis([]);
      setError(datosDelError(e));
    }
  }, [cobro.id]);

  useEffect(() => { cargar(); }, [cargar]);

  if (cfdis === null) return null;
  const vivo = cfdis.find((c) => VIVOS.includes(c.estado)) || null;
  const ultimoCancelado = !vivo ? cfdis.find((c) => c.estado === "cancelado") : null;
  const mostrar = vivo || ultimoCancelado;
  const puedeFacturar = !vivo && cobro.estado === "pagado" && !cobro.anulado_at && puede?.("cfdi");

  const pedirVista = async () => {
    setError(null);
    setVista(null);
    setOcupado(true);
    try {
      const { data } = await api.post(`/cobros/${cobro.id}/cfdi`, { receptor, enviar_correo: enviarCorreo, email: correo.trim() },
        { params: { vista_previa: 1 } });
      setVista(data);
    } catch (e) {
      setError(datosDelError(e));
    } finally {
      setOcupado(false);
    }
  };

  const emitir = async () => {
    setError(null);
    setOcupado(true);
    try {
      const { data } = await api.post(`/cobros/${cobro.id}/cfdi`, { receptor, enviar_correo: enviarCorreo, email: correo.trim() });
      setAviso(data.estado === "vigente" ? T.facturada : T.timbrando);
      setModo("lista");
      setVista(null);
      await cargar();
      onCambio?.();
    } catch (e) {
      setError(datosDelError(e));
      await cargar();
    } finally {
      setOcupado(false);
    }
  };

  const accion = async (fn, okMsg) => {
    setError(null);
    setOcupado(true);
    try {
      await fn();
      if (okMsg) setAviso(okMsg);
      await cargar();
      onCambio?.();
    } catch (e) {
      setError(datosDelError(e));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="mt-5 rounded-xl border border-slate-200 p-4" data-testid="cfdi-cobro">
      <div className="text-xs uppercase tracking-widest text-slate-400 mb-2 inline-flex items-center gap-1.5">
        <FileText className="w-3.5 h-3.5" /> CFDI
      </div>

      {mostrar && modo !== "cancelar" && (
        <div className="space-y-2" data-testid="cfdi-actual">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${CLASE_ESTADO[mostrar.estado] || ""}`}
                  data-testid="cfdi-estado">{T.estados[mostrar.estado] || mostrar.estado}</span>
            {!mostrar.livemode && <span className="text-xs text-amber-700">{T.modoPrueba}</span>}
            <span className="text-slate-600">{mostrar.receptor?.nombre} · {mostrar.receptor?.rfc}</span>
            <span className="font-semibold">{formatoDinero(mostrar.total)}</span>
          </div>
          {mostrar.uuid && <p className="font-mono text-xs text-slate-400 break-all">UUID: {mostrar.uuid}</p>}
          {mostrar.estado === "timbrando" && <p className="text-xs text-amber-700">{T.timbrando}</p>}
          {mostrar.estado === "reservado" && <p className="text-xs text-amber-700">{T.reservada}</p>}
          <div className="flex flex-wrap gap-2">
            {["vigente", "cancelacion_solicitada", "cancelado"].includes(mostrar.estado) && (
              <>
                <button type="button" className={btn} onClick={() => descargarCfdi(mostrar.id, "pdf", mostrar.uuid)} data-testid="cfdi-pdf">
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
                <button type="button" className={btn} onClick={() => descargarCfdi(mostrar.id, "xml", mostrar.uuid)} data-testid="cfdi-xml">
                  <Download className="w-3.5 h-3.5" /> XML
                </button>
              </>
            )}
            {mostrar.estado === "vigente" && (
              <button type="button" className={btn} disabled={ocupado} data-testid="cfdi-enviar"
                      onClick={() => accion(() => api.post(`/cfdis/${mostrar.id}/enviar`, {}), T.enviada)}>
                <Mail className="w-3.5 h-3.5" /> {T.enviar}
              </button>
            )}
            {["reservado", "timbrando", "cancelacion_solicitada"].includes(mostrar.estado) && (
              <button type="button" className={btn} disabled={ocupado} data-testid="cfdi-actualizar"
                      onClick={() => accion(() => api.post(`/cfdis/${mostrar.id}/reconciliar`))}>
                <RefreshCw className="w-3.5 h-3.5" /> {T.actualizar}
              </button>
            )}
            {mostrar.estado === "vigente" && puede?.("cfdi_cancelar") && (
              <button type="button" className={`${btn} text-red-600`} disabled={ocupado} data-testid="cfdi-cancelar"
                      onClick={() => { setError(null); setModo("cancelar"); }}>
                <Ban className="w-3.5 h-3.5" /> {T.cancelar}
              </button>
            )}
          </div>
        </div>
      )}

      {modo === "cancelar" && vivo && (
        <CancelarCfdi ocupado={ocupado} onVolver={() => setModo("lista")}
                      onConfirmar={(motivo) => accion(async () => { await api.post(`/cfdis/${vivo.id}/cancelar`, { motivo }); setModo("lista"); })} />
      )}

      {!mostrar && !puedeFacturar && modo === "lista" && cobro.estado !== "pagado" && (
        <p className="text-xs text-slate-500" data-testid="cfdi-no-pagado">{datosDelErrorTexto("PUE_CON_SALDO")}</p>
      )}

      {puedeFacturar && modo === "lista" && (
        <button type="button" onClick={() => { setError(null); setModo("facturar"); }}
                className="px-3 py-2 text-sm rounded-lg bg-brand-electric text-white hover:bg-blue-700 inline-flex items-center gap-1.5"
                data-testid="cfdi-facturar">
          <FileText className="w-4 h-4" /> {T.facturar}
        </button>
      )}

      {puedeFacturar && modo === "facturar" && (
        <div className="space-y-3" data-testid="cfdi-facturar-form">
          <p className="text-sm font-medium text-slate-900">{T.receptor}</p>
          <div className="flex flex-col gap-1.5 text-sm">
            {[["paciente", T.receptorPaciente], ["publico_general", T.receptorPublico]].map(([v, l]) => (
              <label key={v} className="inline-flex items-center gap-2">
                <input type="radio" name="cfdi-receptor" value={v} checked={receptor === v}
                       onChange={() => { setReceptor(v); setVista(null); setError(null); }} data-testid={`cfdi-receptor-${v}`} />
                {l}
              </label>
            ))}
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enviarCorreo} onChange={(e) => setEnviarCorreo(e.target.checked)} data-testid="cfdi-enviar-correo" />
            {T.enviarCorreo}
          </label>
          {enviarCorreo && (
            <label className="block text-xs font-medium text-slate-600">{T.correo}
              <input type="email" className="mt-1 h-9 w-full rounded-lg border border-slate-200 px-2.5 text-sm" value={correo}
                     onChange={(e) => setCorreo(e.target.value)} data-testid="cfdi-correo" />
            </label>
          )}

          {error?.code === "PACIENTE_SIN_DATOS_FISCALES" && cobro.patient_id && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs text-slate-600 mb-2">{error.detail}</p>
              <FichaFiscal patientId={cobro.patient_id} compacta onGuardada={() => { setError(null); pedirVista(); }} />
            </div>
          )}

          {vista && (
            <div className="rounded-xl border border-slate-200 p-3 text-sm space-y-1.5" data-testid="cfdi-vista">
              <p className={vista.modo === "live" ? "text-emerald-700 text-xs" : "text-amber-700 text-xs"}>
                {vista.modo === "live" ? T.modoReal : T.modoPrueba}
              </p>
              <p><span className="text-slate-500">{T.seFacturaA}</span> {vista.receptor.nombre} · {vista.receptor.rfc}</p>
              <p><span className="text-slate-500">{T.usoCfdi}:</span> {vista.receptor.uso} · <span className="text-slate-500">{T.formaPago}:</span> {vista.forma_pago}</p>
              <ul className="text-xs text-slate-600">
                {vista.lineas.map((l, i) => (
                  <li key={i}>{l.concepto} — {formatoDinero(l.importe)} · {T.claveSat} {l.clave_sat} · {T.iva} {l.iva}</li>
                ))}
              </ul>
              <p className="font-semibold">{T.total}: {formatoDinero(vista.total)}</p>
              {vista.cupo?.maximo != null && <p className="text-xs text-slate-500">{T.cupo(vista.cupo.usados, vista.cupo.maximo)}</p>}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button type="button" className={btn} onClick={() => { setModo("lista"); setVista(null); setError(null); }}>{T.volver}</button>
            {!vista ? (
              <button type="button" disabled={ocupado} onClick={pedirVista} data-testid="cfdi-ver-vista"
                      className="px-3 py-1.5 text-xs rounded-lg bg-brand-deep text-white hover:bg-slate-800 disabled:opacity-60">
                {T.verVistaPrevia}
              </button>
            ) : (
              <button type="button" disabled={ocupado} onClick={emitir} data-testid="cfdi-emitir"
                      className="px-3 py-1.5 text-xs rounded-lg bg-brand-electric text-white hover:bg-blue-700 disabled:opacity-60">
                {ocupado ? T.emitiendo : T.emitir}
              </button>
            )}
          </div>
        </div>
      )}

      {error && error.code !== "PACIENTE_SIN_DATOS_FISCALES" && (
        <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="cfdi-error">{error.detail}</p>
      )}
      {error?.code === "CUPO_CFDI_AGOTADO" && <PaqueteCfdi cupo={error.extra} puede={puede} />}
      {aviso && !error && <p className="mt-3 text-sm text-emerald-700" data-testid="cfdi-aviso">{aviso}</p>}
    </div>
  );
}

// El motivo de por qué no se puede facturar todavía lo da el backend; para el caso más común
// (cobro con saldo) se pinta el mismo texto que devolvería, sin pedirlo.
function datosDelErrorTexto(code) {
  return code === "PUE_CON_SALDO"
    ? "Sólo se factura cuando el cobro está pagado por completo."   // Copy aprobado por Jesús (4 Oct 2026)
    : "";
}
